import { useEffect, useRef, useState } from "react";
import * as THREE from "three";
import { Music2, X } from "lucide-react";
import { AmbientSound, AMBIENT_TRACKS } from "@/lib/ambient";

function makeLabelTexture(text, sub) {
  const c = document.createElement("canvas");
  c.width = 1024; c.height = 256;
  const ctx = c.getContext("2d");
  ctx.clearRect(0, 0, c.width, c.height);
  ctx.fillStyle = "rgba(255,255,255,0.95)";
  ctx.textAlign = "center";
  ctx.font = "bold 110px 'Plus Jakarta Sans', Arial, sans-serif";
  ctx.fillText(text, 512, 120);
  ctx.font = "70px 'DM Sans', Arial, sans-serif";
  ctx.fillText(sub, 512, 215);
  const tex = new THREE.CanvasTexture(c);
  tex.colorSpace = THREE.SRGBColorSpace;
  return tex;
}

export const ImmersiveRoom = ({ scale, color, label, count, onExit, running }) => {
  const mountRef = useRef(null);
  const stateRef = useRef({ scale, color, label, count });
  const [xrSupported, setXrSupported] = useState(false);
  const rendererRef = useRef(null);
  const ambientRef = useRef(null);
  const [track, setTrack] = useState(() => localStorage.getItem("ambient_track") || "none");
  const [volume, setVolume] = useState(() => Number(localStorage.getItem("ambient_volume") ?? 0.5));

  useEffect(() => {
    ambientRef.current = new AmbientSound();
    return () => ambientRef.current?.dispose();
  }, []);

  useEffect(() => {
    const a = ambientRef.current;
    if (!a) return;
    a.setVolume(volume);
    localStorage.setItem("ambient_volume", String(volume));
  }, [volume]);

  useEffect(() => {
    const a = ambientRef.current;
    if (!a) return;
    localStorage.setItem("ambient_track", track);
    if (track === "none") { a.stop(); return; }
    if (running) a.play(track); else a.stop();
  }, [track, running]);

  useEffect(() => { stateRef.current = { scale, color, label, count }; }, [scale, color, label, count]);

  useEffect(() => {
    if (navigator.xr?.isSessionSupported) navigator.xr.isSessionSupported("immersive-vr").then(setXrSupported).catch(() => setXrSupported(false));
  }, []);

  useEffect(() => {
    const mount = mountRef.current;
    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: false });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setSize(mount.clientWidth, mount.clientHeight);
    renderer.xr.enabled = true;
    mount.appendChild(renderer.domElement);
    rendererRef.current = renderer;

    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0x0b1026);
    scene.fog = new THREE.FogExp2(0x0b1026, 0.06);
    const camera = new THREE.PerspectiveCamera(60, mount.clientWidth / mount.clientHeight, 0.1, 100);
    camera.position.set(0, 1.6, 0);

    scene.add(new THREE.HemisphereLight(0xbfd4ff, 0x1a1033, 0.9));
    const key = new THREE.PointLight(0xffffff, 12, 30);
    key.position.set(2, 3, -1);
    scene.add(key);

    const floor = new THREE.Mesh(new THREE.CircleGeometry(12, 64), new THREE.MeshStandardMaterial({ color: 0x141a3a, roughness: 0.9 }));
    floor.rotation.x = -Math.PI / 2;
    scene.add(floor);
    const ring = new THREE.Mesh(new THREE.RingGeometry(1.25, 1.3, 96), new THREE.MeshBasicMaterial({ color: 0x6366f1, transparent: true, opacity: 0.35, side: THREE.DoubleSide }));
    ring.rotation.x = -Math.PI / 2; ring.position.y = 0.01;
    scene.add(ring);

    const starGeo = new THREE.BufferGeometry();
    const pts = [];
    for (let i = 0; i < 600; i += 1) { const r = 20 + Math.random() * 20; const t = Math.random() * Math.PI * 2; const p = Math.acos(2 * Math.random() - 1); pts.push(r * Math.sin(p) * Math.cos(t), Math.abs(r * Math.cos(p)) + 1, r * Math.sin(p) * Math.sin(t)); }
    starGeo.setAttribute("position", new THREE.Float32BufferAttribute(pts, 3));
    scene.add(new THREE.Points(starGeo, new THREE.PointsMaterial({ color: 0x93c5fd, size: 0.08, transparent: true, opacity: 0.7 })));

    const orbMat = new THREE.MeshPhysicalMaterial({ color: new THREE.Color(color), emissive: new THREE.Color(color), emissiveIntensity: 0.35, roughness: 0.25, transmission: 0.2, thickness: 1 });
    const orb = new THREE.Mesh(new THREE.SphereGeometry(0.45, 64, 64), orbMat);
    orb.position.set(0, 1.7, -2.8);
    scene.add(orb);
    const halo = new THREE.Mesh(new THREE.SphereGeometry(0.56, 48, 48), new THREE.MeshBasicMaterial({ color: new THREE.Color(color), transparent: true, opacity: 0.12, side: THREE.BackSide }));
    halo.position.copy(orb.position);
    scene.add(halo);

    const labelTex = makeLabelTexture(label, String(count));
    const labelSprite = new THREE.Sprite(new THREE.SpriteMaterial({ map: labelTex, transparent: true, depthWrite: false }));
    labelSprite.scale.set(2.2, 0.55, 1);
    labelSprite.position.set(0, 2.75, -2.8);
    scene.add(labelSprite);

    let yaw = 0, pitch = 0, dragging = false, lastX = 0, lastY = 0, lastLabel = "";
    const onDown = (e) => { dragging = true; lastX = e.clientX ?? e.touches?.[0]?.clientX; lastY = e.clientY ?? e.touches?.[0]?.clientY; };
    const onMove = (e) => {
      if (!dragging) return;
      const x = e.clientX ?? e.touches?.[0]?.clientX; const y = e.clientY ?? e.touches?.[0]?.clientY;
      yaw -= (x - lastX) * 0.004; pitch = Math.max(-0.6, Math.min(0.6, pitch - (y - lastY) * 0.004));
      lastX = x; lastY = y;
    };
    const onUp = () => { dragging = false; };
    const el = renderer.domElement;
    el.addEventListener("pointerdown", onDown); el.addEventListener("pointermove", onMove); window.addEventListener("pointerup", onUp);

    const currentScale = { v: stateRef.current.scale };
    const tmpColor = new THREE.Color();
    renderer.setAnimationLoop(() => {
      const s = stateRef.current;
      currentScale.v += (s.scale - currentScale.v) * 0.06;
      const sc = 0.7 + currentScale.v * 0.9;
      orb.scale.setScalar(sc);
      halo.scale.setScalar(sc * 1.15 + Math.sin(performance.now() / 900) * 0.03);
      tmpColor.set(s.color);
      orbMat.color.lerp(tmpColor, 0.05); orbMat.emissive.lerp(tmpColor, 0.05); halo.material.color.lerp(tmpColor, 0.05);
      const key2 = `${s.label}|${s.count}`;
      if (key2 !== lastLabel) { lastLabel = key2; labelSprite.material.map?.dispose(); labelSprite.material.map = makeLabelTexture(s.label, String(s.count)); labelSprite.material.needsUpdate = true; }
      if (!renderer.xr.isPresenting) { camera.rotation.set(pitch, yaw, 0, "YXZ"); }
      renderer.render(scene, camera);
    });

    const onResize = () => { camera.aspect = mount.clientWidth / mount.clientHeight; camera.updateProjectionMatrix(); renderer.setSize(mount.clientWidth, mount.clientHeight); };
    window.addEventListener("resize", onResize);
    return () => {
      renderer.setAnimationLoop(null);
      window.removeEventListener("resize", onResize); window.removeEventListener("pointerup", onUp);
      el.removeEventListener("pointerdown", onDown); el.removeEventListener("pointermove", onMove);
      renderer.xr.getSession?.()?.end?.();
      renderer.dispose(); mount.removeChild(renderer.domElement);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const enterVR = async () => {
    try {
      const session = await navigator.xr.requestSession("immersive-vr", { optionalFeatures: ["local-floor", "bounded-floor"] });
      await rendererRef.current.xr.setSession(session);
    } catch (e) {
      console.warn("WebXR session could not start:", e?.message || e);
    }
  };

  return (
    <div className="absolute inset-0 bg-[#0b1026]" data-testid="immersive-room">
      <div ref={mountRef} className="absolute inset-0 cursor-grab active:cursor-grabbing" />
      <div className="absolute top-4 left-4 right-4 flex items-start justify-between gap-3 pointer-events-none">
        <div className="rounded-2xl bg-white/10 backdrop-blur px-4 py-3 text-white text-sm pointer-events-auto">
          <p className="font-display font-bold text-lg" data-testid="immersive-label">{label}{running ? ` · ${count}` : ""}</p>
          <p className="text-white/70 text-xs">{running ? "Follow the orb. Drag to look around." : "Press Start below, then follow the orb."}{track !== "none" && !running ? " Sound starts with the session." : ""}</p>
        </div>
        <div className="flex gap-2 pointer-events-auto items-center">
          <div className="rounded-full bg-white/10 backdrop-blur px-3 py-1.5 flex items-center gap-2 text-white text-xs" data-testid="ambient-controls">
            <Music2 className="w-4 h-4 text-indigo-200" />
            <select value={track} onChange={(e) => setTrack(e.target.value)} className="bg-transparent text-white text-xs font-semibold outline-none [&>option]:text-slate-900" aria-label="Background sound" data-testid="ambient-track-select">
              {AMBIENT_TRACKS.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
            </select>
            <input type="range" min="0" max="1" step="0.05" value={volume} onChange={(e) => setVolume(Number(e.target.value))} className="w-20 accent-indigo-400" aria-label="Background volume" data-testid="ambient-volume" disabled={track === "none"} />
          </div>
          {xrSupported && <button type="button" onClick={enterVR} className="rounded-full bg-indigo-500 hover:bg-indigo-400 text-white text-sm font-semibold px-4 py-2" data-testid="immersive-enter-vr">Enter VR</button>}
          <button type="button" onClick={onExit} className="rounded-full bg-white/15 hover:bg-white/25 text-white w-10 h-10 grid place-items-center" aria-label="Exit immersive room" data-testid="immersive-exit"><X className="w-5 h-5" /></button>
        </div>
      </div>
    </div>
  );
};
